import React, { ChangeEvent, useCallback, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  AppBar,
  styled,
  IconButton,
  Box,
  Stack,
  Toolbar,
  useMediaQuery,
  Modal,
  Card,
  Typography,
  Button,
} from "@mui/material";
import NiceModal from "@ebay/nice-modal-react";
import { useSelector, useDispatch } from "src/store/Store";
import {
  toggleSidebar,
  toggleMobileSidebar,
} from "src/store/customizer/CustomizerSlice";
import { AppState } from "src/store/Store";
import { SAVED_USER_INFO } from "src/common/data/global";
import { IconLogout, IconMenu2 } from "@tabler/icons";
// import systemLogo from "src/assets/images/systemLogo.png";
import ceo from "src/assets/images/ceo.jpg";
import jisa from "src/assets/images/jisa.jpg";
import buse from "src/assets/images/buse.jpg";
import member from "src/assets/images/member.jpg";
import {
  clearAuth,
  selectId,
  selectUser,
  selectUserCompany,
} from "src/store/authSlice";
import { getSelfLabel } from "src/assets/formatter";
import { memberAuthList } from "src/common/data/global";
import { TextField } from "src/components/common/forms";
import { extractOnlyNumber } from "src/utils/stringFormatter";
import { CustomDatePicker } from "src/components/common";
import { updateMemberInfo, updatePassword } from "src/services/accoutApi";
import { formatToFull } from "src/utils/dateFormatter";
import useReloadUserInfo from "src/hooks/useReloadUserInfo";

const getMemberAuthIcon = (auth: string | null) => {
  let value = member;
  switch (auth) {
    case "MA0001":
      value = ceo;
      break;
    case "MA0002":
      value = jisa;
      break;
    case "MA0003":
      value = buse;
      break;
    default:
      value = member;
  }
  return value;
};

const Div = styled(Card)`
  position: absolute;
  top: 60px;
  right: 86px;
  box-shadow: rgba(145, 158, 171, 0.3) 0px 0px 2px 0px,
    rgba(145, 158, 171, 0.12) 0px 12px 24px -4px;
  border-radius: 7px;
  width: 300px;
  overflow: visible;
`;

const PrifileItem = styled("div")`
  display: flex;
  align-items: center;
  gap: 2px;
`;
const PrifileItemTitle = styled("div")`
  font-weight: 600;
  width: 55px;
  text-align: center;
  white-space: nowrap;
`;

const ProfileModal = ({
  open,
  onClose,
}: {
  open: boolean;
  onClose: () => void;
}) => {
  const memberId = useSelector(selectId);
  const {
    auth: memberAuth,
    name: memberName,
    tel: memberTel,
    email: memberEmail,
  } = useSelector(selectUser);
  const { branchName, departmentName, employmentDate } =
    useSelector(selectUserCompany);

  const [editMode, setEditMode] = useState(false);
  const [userInfo, setUserInfo] = useState({
    name: memberName,
    tel: extractOnlyNumber(memberTel),
    email: memberEmail,
    employmentDate: employmentDate,
  });

  const [passwordEditMode, setPasswordEditMode] = useState(false);
  const [passwordInfo, setPasswordInfo] = useState({
    password: "",
    newPassword: "",
    newPasswordCheck: "",
  });

  const { reloadUserInfo } = useReloadUserInfo();

  const handelValueChange = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setUserInfo((curr) => ({
      ...curr,
      [name]: value,
    }));
  }, []);

  const onChangeDate = useCallback((dates: any) => {
    setUserInfo((curr) => ({
      ...curr,
      employmentDate: formatToFull(dates),
    }));
  }, []);

  const handelPasswordValueChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      const { name, value } = e.target;
      setPasswordInfo((curr) => ({
        ...curr,
        [name]: value,
      }));
    },
    []
  );

  const handleEditMode = useCallback(() => {
    setEditMode((curr) => !curr);
  }, []);

  const handlePasswordEditMode = useCallback(() => {
    setPasswordEditMode((curr) => !curr);
  }, []);

  const handleUpdateUserInfo = useCallback(async () => {
    try {
      const { name, tel, email, employmentDate } = userInfo;
      const { data } = await updateMemberInfo({
        mbr_id: memberId,
        mbr_nm: name,
        mbr_tel: tel,
        mbr_email: email,
        mbr_empl_dt: employmentDate,
      });

      if (data.data > 0) {
        NiceModal.show("alert", {
          title: "내 정보 수정을 성공했습니다.",
        }).then(() => {
          reloadUserInfo();
          setEditMode(false);
        });
      } else {
        NiceModal.show("alert", {
          title: "내 정보 수정을 성공하지 못했습니다.",
          message:
            "잠시후 다시 시도해주세요. 문제가 계속될 경우 관리자에게 문의 바랍니다.",
        });
      }
    } catch {
      NiceModal.show("alert", {
        title: "내 정보 수정을 성공하지 못했습니다.",
        message:
          "잠시후 다시 시도해주세요. 문제가 계속될 경우 관리자에게 문의 바랍니다.",
      });
    }
  }, [memberId, userInfo, reloadUserInfo]);

  const handleUpdatePassword = useCallback(async () => {
    const { password, newPassword, newPasswordCheck } = passwordInfo;

    if (newPassword !== newPasswordCheck) {
      NiceModal.show("alert", {
        title: "새 비밀번호와 새 비밀번호 확인이 일치하지 않습니다.",
        message: "새 비밀번호와 새 비밀번호를 확인해주세요.",
      });
    } else {
      try {
        const { data } = await updatePassword({
          mbr_id: memberId,
          mbr_pw: newPassword,
          mbr_pw_old: password,
        });

        if (data.data > 0) {
          NiceModal.show("alert", {
            title: "비밀번호 수정을 성공했습니다.",
          }).then(() => {
            setPasswordInfo({
              password: "",
              newPassword: "",
              newPasswordCheck: "",
            });
            setPasswordEditMode(false);
          });
        } else {
          NiceModal.show("alert", {
            title: "비밀번호 수정을 성공하지 못했습니다.",
            message: "다시 시도해주세요.",
          });
        }
      } catch {
        NiceModal.show("alert", {
          title: "비밀번호 수정을 성공하지 못했습니다.",
          message:
            "다시 시도해주세요. 문제가 계속될 경우 관리자에게 문의 바랍니다.",
        });
      }
    }
  }, [memberId, passwordInfo]);

  return (
    <Modal
      open={open}
      onClose={onClose}
      componentsProps={{
        backdrop: { style: { backgroundColor: "transparent" } },
      }}
    >
      <Div>
        <Typography
          variant="h5"
          sx={{
            marginBottom: "10px",
            display: "flex",
            alignItems: "center",
            gap: "4px",
          }}
        >
          {editMode ? (
            <TextField
              size="small"
              variant="outlined"
              placeholder="이름"
              name="name"
              value={userInfo.name}
              onChange={handelValueChange}
            />
          ) : (
            memberName
          )}
          {" 님의 정보"}
        </Typography>
        <Stack direction="row" alignItems="center">
          <img
            src={getMemberAuthIcon(memberAuth)}
            width="80px"
            style={{ borderRadius: 10, height: "100%" }}
            alt=""
          />
          <Stack gap="4px">
            <PrifileItem>
              <PrifileItemTitle>권한 :</PrifileItemTitle>

              {getSelfLabel(memberAuthList, memberAuth)}
            </PrifileItem>
            <PrifileItem>
              <PrifileItemTitle>지사 :</PrifileItemTitle>
              {branchName}
            </PrifileItem>
            <PrifileItem>
              <PrifileItemTitle>부서 :</PrifileItemTitle>
              {departmentName}
            </PrifileItem>
          </Stack>
        </Stack>
        <Stack gap="4px" marginTop="10px">
          <PrifileItem>
            <PrifileItemTitle>연락처 :</PrifileItemTitle>
            {editMode ? (
              <TextField
                size="small"
                variant="outlined"
                type="number"
                fullWidth
                placeholder="연락처"
                name="tel"
                value={userInfo.tel}
                onChange={handelValueChange}
              />
            ) : (
              memberTel
            )}
          </PrifileItem>
          <PrifileItem>
            <PrifileItemTitle>이메일 :</PrifileItemTitle>
            {editMode ? (
              <TextField
                size="small"
                variant="outlined"
                fullWidth
                placeholder="이메일"
                name="email"
                value={userInfo.email}
                onChange={handelValueChange}
              />
            ) : (
              memberEmail
            )}
          </PrifileItem>
          <PrifileItem>
            <PrifileItemTitle>입사일 :</PrifileItemTitle>
            {editMode ? (
              <CustomDatePicker
                startDate={new Date(userInfo.employmentDate)}
                onChange={onChangeDate}
              />
            ) : (
              employmentDate
            )}
          </PrifileItem>
          <Stack direction="row" marginTop="10px" gap="4px">
            {editMode ? (
              <>
                <Button
                  fullWidth
                  disableRipple
                  size="small"
                  onClick={handleEditMode}
                >
                  취소
                </Button>
                <Button
                  fullWidth
                  disableRipple
                  variant="contained"
                  size="small"
                  onClick={handleUpdateUserInfo}
                >
                  저장
                </Button>
              </>
            ) : (
              <Button
                fullWidth
                disableRipple
                variant="contained"
                size="small"
                onClick={handleEditMode}
              >
                정보수정하기
              </Button>
            )}
          </Stack>
        </Stack>

        <Stack gap="4px">
          {passwordEditMode && (
            <>
              <PrifileItemTitle style={{ marginTop: "10px" }}>
                비밀번호 :
              </PrifileItemTitle>
              <TextField
                type="password"
                size="small"
                variant="outlined"
                fullWidth
                placeholder="비밀번호"
                name="password"
                value={passwordInfo.password}
                onChange={handelPasswordValueChange}
              />

              <PrifileItemTitle>새 비밀번호 :</PrifileItemTitle>
              <TextField
                type="password"
                size="small"
                variant="outlined"
                fullWidth
                placeholder="새 비밀번호"
                name="newPassword"
                value={passwordInfo.newPassword}
                onChange={handelPasswordValueChange}
              />

              <PrifileItemTitle>새 비밀번호 확인 :</PrifileItemTitle>
              <TextField
                type="password"
                size="small"
                variant="outlined"
                fullWidth
                placeholder="새 비밀번호 확인"
                name="newPasswordCheck"
                value={passwordInfo.newPasswordCheck}
                onChange={handelPasswordValueChange}
              />
            </>
          )}

          <Stack direction="row" marginTop="10px" gap="4px">
            {passwordEditMode ? (
              <>
                <Button
                  fullWidth
                  disableRipple
                  size="small"
                  onClick={handlePasswordEditMode}
                >
                  취소
                </Button>
                <Button
                  fullWidth
                  disableRipple
                  variant="contained"
                  size="small"
                  onClick={handleUpdatePassword}
                  disabled={Object.values(passwordInfo).some((item) => !item)}
                >
                  저장
                </Button>
              </>
            ) : (
              <Button
                fullWidth
                disableRipple
                variant="contained"
                size="small"
                onClick={handlePasswordEditMode}
              >
                비밀번호 수정하기
              </Button>
            )}
          </Stack>
        </Stack>
      </Div>
    </Modal>
  );
};

const Header = () => {
  const lgUp = useMediaQuery((theme: any) => theme.breakpoints.up("lg"));

  const { auth: memberAuth, name: memberName } = useSelector(selectUser);

  const customizer = useSelector((state: AppState) => state.customizer);
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [profileOpen, setProfileOpen] = useState(false);

  const handleProfileOpen = useCallback(() => {
    setProfileOpen(true);
  }, []);

  const handleProfileClose = () => {
    setProfileOpen(false);
  };

  const AppBarStyled = styled(AppBar)(({ theme }) => ({
    boxShadow: "none",
    background: theme.palette.background.paper,
    borderBottom: "1px solid #e5eaef",
    justifyContent: "center",
    backdropFilter: "blur(4px)",
    [theme.breakpoints.up("lg")]: {
      minHeight: customizer.TopbarHeight,
    },
  }));

  const ToolbarStyled = styled(Toolbar)(({ theme }) => ({
    width: "100%",
    color: theme.palette.text.secondary,
  }));

  const handleLogout = useCallback(() => {
    sessionStorage.removeItem("ssuser");
    for (const item of SAVED_USER_INFO) {
      localStorage.removeItem(item);
    }

    dispatch(clearAuth());
    navigate("/signin");
  }, [navigate, dispatch]);

  return (
    <AppBarStyled position="sticky" color="default">
      <ToolbarStyled>
        {/* ------------------------------------------- */}
        {/* Toggle Button Sidebar */}
        {/* ------------------------------------------- */}
        <IconButton
          color="inherit"
          aria-label="menu"
          onClick={
            lgUp
              ? () => dispatch(toggleSidebar())
              : () => dispatch(toggleMobileSidebar())
          }
        >
          <IconMenu2 size="20" />
        </IconButton>
        <div className="flex items-center">
          {/* <img
            src={systemLogo}
            width="200px"
            style={{ margin: "auto" }}
            alt=""
          /> */}
        </div>

        {/* ------------------------------------------- */}
        {/* Search Dropdown */}
        {/* ------------------------------------------- */}
        {/* <Search />
        {lgUp ? (
          <>
            <Navigation />
          </>
        ) : null} */}

        <Box flexGrow={1} />
        <Stack spacing={1} direction="row" alignItems="center">
          {/* <Notifications /> */}
          {/* ------------------------------------------- */}
          {/* Toggle Right Sidebar for mobile */}
          {/* ------------------------------------------- */}
          {/* {lgDown ? <MobileRightSidebar /> : null}
          <Profile /> */}
          {/*  */}

          <IconButton
            sx={{
              border: "1px solid #7D7E82",
              borderRadius: 2,
            }}
            color="inherit"
            aria-label="logout"
            onClick={handleLogout}
          >
            <IconLogout size="30" />
          </IconButton>
        </Stack>
      </ToolbarStyled>
    </AppBarStyled>
  );
};

export default Header;
